import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from '../../atoms/Button';
import LocationMiniMap from '../LocationMiniMap';
import PanelDropdownField from '../PanelDropdownField';
import PanelInputField from '../PanelInputField';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import { userProfileService } from '../../../services/userProfileService';
import useAddressAutocompletePicker from '../../../hooks/useAddressAutocompletePicker';
import {
  resolveTypedAddress,
  TYPED_ADDRESS_RESOLUTION_STATUS,
} from '../../../services/geocoding/addressResolution';
import { resolveLocationFromCoordinates } from '../../../services/geocoding/locationResolution';
import {
  DEFAULT_LOCATION_COUNTRY_OPTION,
  SUPPORTED_LOCATION_COUNTRY_OPTIONS,
  buildDisplayFromParts,
  formatLocationDisplay,
  isSupportedLocationCountry,
} from '../../../utils/location';
import './index.scss';

const LOCATION_ADDRESS_LISTBOX_ID = 'location-address-listbox';
const ADDRESS_LISTBOX_GAP = 8;
const ADDRESS_LISTBOX_MAX_HEIGHT = 240;
const ADDRESS_LISTBOX_MIN_HEIGHT = 96;
const ADDRESS_LISTBOX_VIEWPORT_PADDING = 12;
const MAP_LOCATION_RESOLUTION_FAILED_MESSAGE = 'Unable to resolve the selected point. Move the pin or choose an address.';
const MAP_LOCATION_MISS_MESSAGE = 'We could not determine an address for the selected point. You can still save the map position or choose an address.';
const MAP_LOCATION_PENDING_SAVE_MESSAGE = 'Please wait while we resolve the selected map location.';
const ADDRESS_RESOLUTION_FAILED_MESSAGE = 'Select a valid address suggestion before saving your location.';
const isLatitudeInRange = (latitude) => Number.isFinite(latitude) && latitude >= -90 && latitude <= 90;
const isLongitudeInRange = (longitude) => Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;

const buildDefaultLocationState = (overrides = {}) => ({
  country: DEFAULT_LOCATION_COUNTRY_OPTION.name,
  countryCode: DEFAULT_LOCATION_COUNTRY_OPTION.code,
  stateOrRegion: '',
  city: '',
  latitude: '',
  longitude: '',
  formatted: '',
  fullAddress: '',
  source: 'MANUAL',
  ...overrides,
});

const buildLocalityDisplay = (location = {}) => {
  const city = location.cityName ?? location.city;
  const stateOrRegion = location.regionName ?? location.stateOrRegion;
  const hasSpecificLocality = [city, stateOrRegion].some((part) => (
    typeof part === 'string' && part.trim().length > 0
  ));

  if (!hasSpecificLocality) {
    return '';
  }

  return buildDisplayFromParts({
    city,
    stateOrRegion,
    country: location.countryName ?? location.country,
  });
};

const stripLikelyStreetAddress = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 4) {
    return parts.slice(1).join(', ');
  }
  if (parts.length >= 3 && /^\d+[a-z]?\b/i.test(parts[0])) {
    return parts.slice(1).join(', ');
  }
  return value.trim();
};

const buildFullAddressDisplay = (location = {}) => (
  location.label
  || location.fullAddress
  || location.formatted
  || buildLocalityDisplay(location)
);

const buildAddressDisplay = (location = {}, hideStreetAddress = true) => {
  const localityDisplay = buildLocalityDisplay(location);
  if (hideStreetAddress) {
    return localityDisplay || stripLikelyStreetAddress(buildFullAddressDisplay(location));
  }
  return buildFullAddressDisplay(location) || localityDisplay;
};

const buildInitialState = (location, display) => {
  if (!isSupportedLocationCountry(location)) {
    return buildDefaultLocationState();
  }

  return buildDefaultLocationState({
    stateOrRegion: location?.stateOrRegion ?? '',
    city: location?.city ?? '',
    latitude: typeof location?.latitude === 'number' ? String(location.latitude) : '',
    longitude: typeof location?.longitude === 'number' ? String(location.longitude) : '',
    formatted: display || location?.formatted || '',
    fullAddress: display || location?.formatted || '',
    source: location?.source ?? 'MANUAL',
  });
};

const buildInitialAddress = (location, display) => {
  if (!isSupportedLocationCountry(location)) {
    return '';
  }

  if (typeof display === 'string' && display.trim().length > 0) {
    return display.trim();
  }

  if (typeof location?.formatted === 'string' && location.formatted.trim().length > 0) {
    return location.formatted.trim();
  }

  return buildDisplayFromParts({
    city: location?.city,
    stateOrRegion: location?.stateOrRegion,
    country: location?.country,
  });
};

export const LocationModal = ({
  userId = undefined,
  initialLocation = null,
  initialDisplay = '',
  onClose,
  onSaved = undefined,
  onSave = undefined,
}) => {
  const [formState, setFormState] = useState(() => buildInitialState(initialLocation, initialDisplay));
  const [hideStreetAddress, setHideStreetAddress] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [isMapResolving, setIsMapResolving] = useState(false);
  const [isAddressResolving, setIsAddressResolving] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const mountedRef = useRef(true);
  const addressListboxRef = useRef(null);
  const locationResolutionRequestIdRef = useRef(0);
  const locale = useMemo(() => (typeof navigator !== 'undefined' ? navigator.language : 'en'), []);
  const isAutocompleteEnabled = !isSaving && !isLocating && !isAddressResolving;
  const addressListboxPortalRoot = useMemo(() => (
    typeof document === 'undefined' ? null : document.body
  ), []);
  const [addressListboxLayout, setAddressListboxLayout] = useState(null);

  // Forward-declare via ref so the picker's onSelect (built during hook init)
  // can reach applyGeocodedLocation, which depends on setAddressValue from
  // the hook. This breaks the declaration cycle without duplicating state.
  const applyGeocodedLocationRef = useRef(() => false);
  const handlePickerSelect = useCallback((geocoded) => {
    applyGeocodedLocationRef.current(geocoded, 'MANUAL');
  }, []);
  const addressAutocompleteInsideRefs = useMemo(() => [addressListboxRef], []);

  const {
    containerRef: addressFieldRef,
    inputValue: addressValue,
    suggestions,
    isLoading: isAddressLoading,
    error: addressError,
    isOpen: isAddressOpen,
    highlightedIndex,
    setInputValue: setAddressValue,
    setHighlightedIndex,
    clear: clearAddressAutocomplete,
    openDropdown,
    pickSuggestion,
    handleKeyDown: handleAddressKeyDown,
    handleBlur: handleAddressFieldBlur,
    showNoResults: derivedShowAddressNoResults,
    getActiveDescendantId,
  } = useAddressAutocompletePicker({
    minChars: 3,
    debounceMs: 300,
    countryCode: DEFAULT_LOCATION_COUNTRY_OPTION.code,
    limit: 8,
    allowEmptyForwardFallback: true,
    enabled: isAutocompleteEnabled,
    additionalInsideRefs: addressAutocompleteInsideRefs,
    onSelect: handlePickerSelect,
  });

  const cancelPendingLocationResolution = useCallback(() => {
    locationResolutionRequestIdRef.current += 1;
    setIsMapResolving(false);
    setIsLocating(false);
  }, []);

  const resetResolvedLocationDraft = useCallback((overrides = {}) => {
    setFormState(buildDefaultLocationState(overrides));
    setAddressValue('', { suppressLookup: true });
    setIsAutofilled(false);
  }, [setAddressValue]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    cancelPendingLocationResolution();
    const nextState = buildInitialState(initialLocation, initialDisplay);
    const nextAddress = buildAddressDisplay({
      city: nextState.city,
      stateOrRegion: nextState.stateOrRegion,
      country: nextState.country,
      formatted: nextState.formatted,
      label: nextState.fullAddress,
    }, true);
    setFormState({
      ...nextState,
      formatted: nextAddress,
      fullAddress: nextState.fullAddress || nextAddress,
    });
    setHideStreetAddress(true);
    setIsAutofilled(false);
    setAddressValue(nextAddress || buildInitialAddress(initialLocation, initialDisplay), { suppressLookup: true });
  }, [cancelPendingLocationResolution, initialLocation, initialDisplay, setAddressValue]);

  // Click-outside and keyboard nav for the address dropdown live inside
  // `useAddressAutocompletePicker` — the hook owns both.

  const parseCoordinate = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const selectedCoordinates = useMemo(() => {
    const latitude = parseCoordinate(formState.latitude);
    const longitude = parseCoordinate(formState.longitude);

    if (!isLatitudeInRange(latitude) || !isLongitudeInRange(longitude)) {
      return null;
    }

    return {
      lat: latitude,
      lng: longitude,
    };
  }, [formState.latitude, formState.longitude]);

  const buildLocationStateFromGeocoded = useCallback((location, source = 'MANUAL') => {
    if (!location) {
      return null;
    }

    if (!isSupportedLocationCountry({
      country: location.countryName,
      countryCode: location.countryCode,
    })) {
      return null;
    }

    const fullAddress = buildFullAddressDisplay({
      ...location,
      countryName: DEFAULT_LOCATION_COUNTRY_OPTION.name,
    });
    const nextFormatted = buildAddressDisplay({
      ...location,
      countryName: DEFAULT_LOCATION_COUNTRY_OPTION.name,
      label: fullAddress,
      fullAddress,
    }, hideStreetAddress);

    return {
      country: DEFAULT_LOCATION_COUNTRY_OPTION.name,
      countryCode: DEFAULT_LOCATION_COUNTRY_OPTION.code,
      stateOrRegion: location.regionName || '',
      city: location.cityName || '',
      latitude: typeof location.latitude === 'number' ? String(location.latitude) : '',
      longitude: typeof location.longitude === 'number' ? String(location.longitude) : '',
      formatted: nextFormatted || '',
      fullAddress: fullAddress || nextFormatted || '',
      source,
    };
  }, [hideStreetAddress]);

  const applyGeocodedLocation = useCallback((location, source = 'MANUAL') => {
    if (!location) {
      return false;
    }

    if (!isSupportedLocationCountry({
      country: location.countryName,
      countryCode: location.countryCode,
    })) {
      setError('MUZONE currently supports United States locations only. Please enter a US location.');
      setIsAutofilled(false);
      return false;
    }

    const nextState = buildLocationStateFromGeocoded(location, source);
    if (!nextState) {
      setIsAutofilled(false);
      return false;
    }

    setFormState((prev) => ({ ...prev, ...nextState }));

    setAddressValue(nextState.formatted || '', { suppressLookup: true });
    setIsAutofilled(true);
    setError(null);
    return true;
  }, [buildLocationStateFromGeocoded, setAddressValue]);

  // Keep the ref pointed at the latest applyGeocodedLocation wrapped with
  // a cancel-before-apply so in-flight map reverse-geocodes don't race
  // the user's suggestion pick. The picker hook's onSelect (bound once
  // at init) dereferences this ref each time, always getting the fresh
  // closure.
  useEffect(() => {
    applyGeocodedLocationRef.current = (geocoded, source = 'MANUAL') => {
      cancelPendingLocationResolution();
      return applyGeocodedLocation(geocoded, source);
    };
  }, [applyGeocodedLocation, cancelPendingLocationResolution]);

  const handleAddressChange = (event) => {
    cancelPendingLocationResolution();
    const value = event.target.value;
    setAddressValue(value);
    setFormState((prev) => {
      const nextSource = prev.source === 'GEOLOCATION' ? 'GEOLOCATION' : 'MANUAL';
      return {
        ...prev,
        stateOrRegion: '',
        city: '',
        latitude: '',
        longitude: '',
        formatted: '',
        fullAddress: '',
        source: nextSource,
      };
    });
    setIsAutofilled(false);
  };

  const handleStreetPrivacyChange = useCallback((event) => {
    const nextHideStreetAddress = event.target.checked;
    setHideStreetAddress(nextHideStreetAddress);
    setFormState((prev) => {
      const nextDisplay = buildAddressDisplay({
        city: prev.city,
        stateOrRegion: prev.stateOrRegion,
        country: prev.country,
        label: prev.fullAddress || prev.formatted,
        fullAddress: prev.fullAddress || prev.formatted,
        formatted: prev.formatted,
      }, nextHideStreetAddress);
      setAddressValue(nextDisplay || addressValue, { suppressLookup: true });
      return {
        ...prev,
        formatted: nextDisplay || '',
      };
    });
  }, [addressValue, setAddressValue]);

  const handleAddressContainerBlur = useCallback((event) => {
    const nextFocused = event?.relatedTarget;
    if (addressListboxRef.current?.contains(nextFocused)) {
      return;
    }
    handleAddressFieldBlur(event);
  }, [handleAddressFieldBlur]);

  const handleAddressListboxBlur = useCallback((event) => {
    const nextFocused = event?.relatedTarget;
    if (
      addressFieldRef.current?.contains(nextFocused)
      || addressListboxRef.current?.contains(nextFocused)
    ) {
      return;
    }
    handleAddressFieldBlur(event);
  }, [addressFieldRef, handleAddressFieldBlur]);

  // `handleSuggestionSelect`, `handleAddressKeyDown`, and
  // `handleAddressFieldBlur` all come from `useAddressAutocompletePicker`
  // (see hook call above). This previously duplicated ~45 lines of
  // keyboard nav, click-outside, and suggestion → geocoded conversion
  // that also lived in DiscoverPage and AddressAutocompleteInput.

  const handleCountryMenuSelect = useCallback((countryCode, countryName) => {
    cancelPendingLocationResolution();
    setFormState((prev) => {
      if (prev.countryCode === countryCode && prev.country === countryName) {
        return prev;
      }
      return {
        ...prev,
        country: countryName,
        countryCode,
      };
    });
    setIsAutofilled(false);
  }, [cancelPendingLocationResolution]);

  const validateForm = useCallback((draft = formState) => {
    const stateOrRegion = draft.stateOrRegion.trim();
    const city = draft.city.trim();
    const hasLatitude = draft.latitude.trim().length > 0;
    const hasLongitude = draft.longitude.trim().length > 0;

    if (hasLatitude !== hasLongitude) {
      return 'Provide both latitude and longitude, or leave both empty.';
    }

    if (!stateOrRegion && !city && !hasLatitude && !hasLongitude) {
      return 'Select an address or adjust the map pin to save your location.';
    }

    const latitude = parseCoordinate(draft.latitude);
    if (latitude !== null && !isLatitudeInRange(latitude)) {
      return 'Latitude must be a number between -90 and 90.';
    }

    const longitude = parseCoordinate(draft.longitude);
    if (longitude !== null && !isLongitudeInRange(longitude)) {
      return 'Longitude must be a number between -180 and 180.';
    }

    return null;
  }, [formState]);

  const buildPayload = (draft = formState) => {
    const trimmedState = draft.stateOrRegion.trim();
    const trimmedCity = draft.city.trim();
    const latitude = parseCoordinate(draft.latitude);
    const longitude = parseCoordinate(draft.longitude);

    const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
    const baseSource = draft.source || 'MANUAL';
    const finalSource = hasCoordinates ? (baseSource === 'GEOLOCATION' ? 'GEOLOCATION' : baseSource) : 'MANUAL';
    const resolvedCountry = DEFAULT_LOCATION_COUNTRY_OPTION.name;
    const resolvedCountryCode = DEFAULT_LOCATION_COUNTRY_OPTION.code;
    const fullAddress = typeof draft.fullAddress === 'string' ? draft.fullAddress.trim() : '';
    const localityDisplay = buildLocalityDisplay({
      city: trimmedCity,
      stateOrRegion: trimmedState,
      country: resolvedCountry,
    });
    const explicitFormatted = hideStreetAddress
      ? localityDisplay
      : (typeof draft.formatted === 'string' ? draft.formatted.trim() : '') || fullAddress || localityDisplay;
    const locationDisplay = formatLocationDisplay(
      {
        country: resolvedCountry,
        stateOrRegion: trimmedState,
        city: trimmedCity,
      },
      explicitFormatted
    );
    const resolvedFormatted = explicitFormatted || locationDisplay || buildDisplayFromParts({
      city: trimmedCity,
      stateOrRegion: trimmedState,
      country: resolvedCountry,
    });

    const location = {
      country: resolvedCountry || null,
      countryCode: resolvedCountryCode,
      stateOrRegion: trimmedState || null,
      city: trimmedCity || null,
      latitude: hasCoordinates ? latitude : null,
      longitude: hasCoordinates ? longitude : null,
      formatted: resolvedFormatted || null,
      source: finalSource,
    };

    return {
      location,
      locationDisplay: locationDisplay || null,
    };
  };

  const hasResolvedLocationDraft = (draft = formState) => {
    const hasLocality = draft.stateOrRegion.trim().length > 0 || draft.city.trim().length > 0;
    const latitude = parseCoordinate(draft.latitude);
    const longitude = parseCoordinate(draft.longitude);
    return hasLocality || (Number.isFinite(latitude) && Number.isFinite(longitude));
  };

  const resolveTypedAddressDraft = async () => {
    const trimmedAddress = addressValue.trim();
    if (!trimmedAddress || hasResolvedLocationDraft()) {
      return formState;
    }
    setIsAddressResolving(true);
    try {
      const resolution = await resolveTypedAddress({
        text: trimmedAddress,
        countryCode: DEFAULT_LOCATION_COUNTRY_OPTION.code,
        limit: 1,
      });
      if (resolution.status !== TYPED_ADDRESS_RESOLUTION_STATUS.RESOLVED) {
        return null;
      }
      const resolved = resolution.location;
      if (!isSupportedLocationCountry({
        country: resolved.countryName,
        countryCode: resolved.countryCode,
      })) {
        setError('MUZONE currently supports United States locations only. Please enter a US location.');
        return null;
      }
      const nextState = buildLocationStateFromGeocoded(resolved, 'MANUAL');
      if (!nextState) {
        return null;
      }
      setFormState((prev) => ({ ...prev, ...nextState }));
      setAddressValue(nextState.formatted || '', { suppressLookup: true });
      setIsAutofilled(true);
      return { ...formState, ...nextState };
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      if (mountedRef.current) {
        setIsAddressResolving(false);
      }
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setError(null);

    if (isMapResolving) {
      setError(MAP_LOCATION_PENDING_SAVE_MESSAGE);
      return;
    }

    const resolvedDraft = await resolveTypedAddressDraft();
    if (!mountedRef.current) {
      return;
    }
    if (!resolvedDraft) {
      setError((currentError) => currentError || ADDRESS_RESOLUTION_FAILED_MESSAGE);
      return;
    }

    const validationError = validateForm(resolvedDraft);
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildPayload(resolvedDraft);
    if (!userId && typeof onSave !== 'function') {
      setError('You must be signed in to update your location.');
      return;
    }

    setIsSaving(true);
    try {
      const response = typeof onSave === 'function'
        ? await onSave(payload)
        : await userProfileService.updateLocation(userId, payload);
      if (!mountedRef.current) {
        return;
      }
      onSaved?.(response);
      onClose?.();
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      console.error(err);
      setError('Failed to update your location. Please try again.');
    } finally {
      if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const resetModalLocationState = useCallback(() => {
    cancelPendingLocationResolution();
    setFormState(buildInitialState(null, ''));
    setHideStreetAddress(true);
    clearAddressAutocomplete();
    setError(null);
    setIsAutofilled(false);
  }, [cancelPendingLocationResolution, clearAddressAutocomplete]);

  const handleMapCoordinatesChange = useCallback(async (coordinates) => {
    if (isSaving || isLocating || isAddressResolving) {
      return;
    }

    const latitude = parseCoordinate(coordinates?.lat);
    const longitude = parseCoordinate(coordinates?.lng);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    const requestId = locationResolutionRequestIdRef.current + 1;
    locationResolutionRequestIdRef.current = requestId;
    setError(null);
    setIsMapResolving(true);
    setFormState((prev) => ({
      ...prev,
      stateOrRegion: '',
      city: '',
      latitude: String(latitude),
      longitude: String(longitude),
      formatted: '',
      fullAddress: '',
      source: 'MANUAL',
    }));
    setAddressValue('', { suppressLookup: true });
    setIsAutofilled(false);

    try {
      const { location, failed, error: resolveError } = await resolveLocationFromCoordinates({
        latitude,
        longitude,
        locale,
      });

      if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
        return;
      }

      if (location) {
        const didApplyLocation = applyGeocodedLocation(location, 'MANUAL');
        if (!didApplyLocation) {
          resetResolvedLocationDraft();
        }
        return;
      }

      if (failed) {
        if (resolveError) {
          console.error(resolveError);
        }
        setError(MAP_LOCATION_RESOLUTION_FAILED_MESSAGE);
      } else {
        setError(MAP_LOCATION_MISS_MESSAGE);
      }
    } catch (err) {
      if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
        return;
      }
      console.error(err);
      setError(MAP_LOCATION_RESOLUTION_FAILED_MESSAGE);
    } finally {
      if (mountedRef.current && locationResolutionRequestIdRef.current === requestId) {
        setIsMapResolving(false);
      }
    }
  }, [
    applyGeocodedLocation,
    isAddressResolving,
    isLocating,
    isSaving,
    locale,
    resetResolvedLocationDraft,
    setAddressValue,
  ]);

  const handleClear = async () => {
    if (isSaving || isLocating) {
      return;
    }

    cancelPendingLocationResolution();
    setError(null);
    if (!userId && typeof onSave !== 'function') {
      setError('You must be signed in to update your location.');
      return;
    }

    setIsSaving(true);
    try {
      const response = typeof onSave === 'function'
        ? await onSave({ location: null, locationDisplay: null })
        : await userProfileService.updateLocation(userId, {
          location: null,
          locationDisplay: null,
        });
      if (!mountedRef.current) {
        return;
      }
      resetModalLocationState();
      onSaved?.(response);
      onClose?.();
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      console.error(err);
      setError('Failed to clear your location. Please try again.');
    } finally {
      if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const updateAddressListboxLayout = useCallback(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const inputNode = document.getElementById('location-address');
    if (!inputNode) {
      setAddressListboxLayout(null);
      return;
    }

    const inputRect = inputNode.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const maxWidth = Math.max(0, viewportWidth - ADDRESS_LISTBOX_VIEWPORT_PADDING * 2);
    const width = Math.min(inputRect.width, maxWidth);
    const left = Math.min(
      Math.max(inputRect.left, ADDRESS_LISTBOX_VIEWPORT_PADDING),
      Math.max(ADDRESS_LISTBOX_VIEWPORT_PADDING, viewportWidth - width - ADDRESS_LISTBOX_VIEWPORT_PADDING),
    );
    const availableBelow = Math.max(
      0,
      viewportHeight - inputRect.bottom - ADDRESS_LISTBOX_GAP - ADDRESS_LISTBOX_VIEWPORT_PADDING,
    );
    const availableAbove = Math.max(
      0,
      inputRect.top - ADDRESS_LISTBOX_GAP - ADDRESS_LISTBOX_VIEWPORT_PADDING,
    );
    const shouldPlaceAbove = availableBelow < ADDRESS_LISTBOX_MIN_HEIGHT && availableAbove > availableBelow;
    const availableHeight = shouldPlaceAbove ? availableAbove : availableBelow;
    const maxHeight = Math.min(
      ADDRESS_LISTBOX_MAX_HEIGHT,
      Math.max(ADDRESS_LISTBOX_MIN_HEIGHT, availableHeight),
    );
    const top = shouldPlaceAbove ? undefined : inputRect.bottom + ADDRESS_LISTBOX_GAP;
    const bottom = shouldPlaceAbove
      ? Math.max(
        ADDRESS_LISTBOX_VIEWPORT_PADDING,
        viewportHeight - inputRect.top + ADDRESS_LISTBOX_GAP,
      )
      : undefined;

    setAddressListboxLayout({
      bottom,
      left,
      maxHeight,
      top,
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isAddressOpen || suggestions.length === 0 || !addressListboxPortalRoot) {
      setAddressListboxLayout(null);
      return undefined;
    }

    updateAddressListboxLayout();

    const handleViewportChange = () => updateAddressListboxLayout();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [
    addressListboxPortalRoot,
    addressValue,
    isAddressOpen,
    suggestions.length,
    updateAddressListboxLayout,
  ]);

  const handleUseCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation is not supported in your browser. Please enter your location manually.');
      return;
    }
    const requestId = locationResolutionRequestIdRef.current + 1;
    locationResolutionRequestIdRef.current = requestId;
    setError(null);
    setIsMapResolving(false);
    setIsLocating(true);

    const geoOptions = {
      maximumAge: 300000,
      timeout: 10000,
      enableHighAccuracy: false,
    };

    navigator.geolocation.getCurrentPosition(async (position) => {
      if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
        return;
      }
      const { latitude, longitude } = position.coords;
      try {
        const { location: resolvedLocation, failed, error: resolveError } = await resolveLocationFromCoordinates({
          latitude,
          longitude,
          locale,
        });

        if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
          return;
        }

        if (resolvedLocation) {
          const didApplyLocation = applyGeocodedLocation(resolvedLocation, 'GEOLOCATION');
          if (!didApplyLocation) {
            resetResolvedLocationDraft({ source: 'GEOLOCATION' });
          }
        } else {
          resetResolvedLocationDraft({
            city: '',
            latitude: String(latitude),
            longitude: String(longitude),
            source: 'GEOLOCATION',
          });
          if (failed) {
            console.error(resolveError);
            setError('Unable to resolve your location. Please choose an address or adjust the map pin.');
          } else {
            setError('We could not automatically determine your city. Please choose an address or save the map position.');
          }
        }
      } catch (err) {
        if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
          return;
        }
        resetResolvedLocationDraft({
          latitude: String(latitude),
          longitude: String(longitude),
          source: 'GEOLOCATION',
        });
        console.error(err);
        setError('Unable to resolve your location. Please choose an address or adjust the map pin.');
      } finally {
        if (mountedRef.current && locationResolutionRequestIdRef.current === requestId) {
          setIsLocating(false);
        }
      }
    }, (geoError) => {
      if (!mountedRef.current || locationResolutionRequestIdRef.current !== requestId) {
        return;
      }
      let message = 'Unable to access your location.';
      if (geoError.code === geoError.PERMISSION_DENIED) {
        message = 'Permission denied. Please allow access to your location or enter it manually.';
      } else if (geoError.code === geoError.POSITION_UNAVAILABLE) {
        message = 'Location data is unavailable. Please try again later.';
      } else if (geoError.code === geoError.TIMEOUT) {
        message = 'Timed out while retrieving your location. Please try again.';
      }
      setError(message);
      setIsLocating(false);
    }, geoOptions);
  };

  const initialLatitude = parseCoordinate(initialLocation?.latitude);
  const initialLongitude = parseCoordinate(initialLocation?.longitude);
  const hasExistingLocation = Boolean(
    initialLocation?.country
    || initialLocation?.stateOrRegion
    || initialLocation?.city
    || Number.isFinite(initialLatitude)
    || Number.isFinite(initialLongitude)
    || initialDisplay
  );

  const showAddressNoResults = derivedShowAddressNoResults;
  const addressActiveDescendant = getActiveDescendantId('location-address');
  const isBusy = isSaving || isLocating || isAddressResolving;
  const addressStatus = (() => {
    if (isAddressLoading) {
      return <span className="location-modal__hint">Searching addresses…</span>;
    }
    if (showAddressNoResults) {
      return <span className="location-modal__autocomplete-empty">No results</span>;
    }
    if (addressError) {
      return (
        <span className="location-modal__autocomplete-error">
          {addressError.message || 'Unable to load address suggestions.'}
        </span>
      );
    }
    if (isAutofilled) {
      return (
        <span className="location-modal__hint">
          Address selected. You can adjust the pin before saving.
        </span>
      );
    }
    return null;
  })();
  const shouldRenderAddressListbox = Boolean(
    isAddressOpen
    && suggestions.length > 0
    && addressListboxPortalRoot
    && addressListboxLayout,
  );
  const addressListbox = shouldRenderAddressListbox ? (
    <ul
      id={LOCATION_ADDRESS_LISTBOX_ID}
      className="location-modal__autocomplete-list"
      role="listbox"
      ref={addressListboxRef}
      onBlur={handleAddressListboxBlur}
      style={{
        bottom: addressListboxLayout.bottom,
        left: addressListboxLayout.left,
        maxHeight: addressListboxLayout.maxHeight,
        top: addressListboxLayout.top,
        width: addressListboxLayout.width,
      }}
    >
      {suggestions.map((suggestion, index) => {
        const isHighlighted = index === highlightedIndex;
        return (
          <li key={`${suggestion.id || suggestion.label}-${index}`} role="presentation">
            <button
              id={`location-address-option-${index}`}
              type="button"
              role="option"
              aria-selected={isHighlighted}
              className={`location-modal__autocomplete-option${isHighlighted ? ' is-highlighted' : ''}`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onFocus={() => setHighlightedIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                pickSuggestion(suggestion);
              }}
              onClick={(event) => {
                if (event.detail === 0) {
                  event.preventDefault();
                  pickSuggestion(suggestion);
                }
              }}
            >
              {suggestion.label}
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <>
      <PopupOverlay
        onClose={isSaving || isAddressResolving ? undefined : onClose}
        closeOnOverlayClick={!isSaving && !isAddressResolving}
        closeOnEsc
      >
        <div className="location-modal" role="dialog" aria-modal="true" aria-labelledby="location-modal-title">
          <header className="location-modal__header">
            <h2 id="location-modal-title">Set your location</h2>
            <p>Share where you are to help other musicians find you.</p>
          </header>

          <form className="location-modal__form" onSubmit={handleSubmit}>
            <div className="location-modal__mini-map">
              <LocationMiniMap
                coordinates={selectedCoordinates}
                disabled={isBusy}
                isResolving={isMapResolving}
                onCoordinatesChange={handleMapCoordinatesChange}
              />
            </div>

            <div className="location-modal__content">
              <PanelDropdownField
                className="location-modal__field location-modal__country-field"
                label="Country"
                id="location-country"
                value={formState.countryCode || DEFAULT_LOCATION_COUNTRY_OPTION.code}
                options={SUPPORTED_LOCATION_COUNTRY_OPTIONS}
                disabled={isBusy}
                triggerClassName="location-modal__country-trigger"
                menuClassName="location-modal__country-menu"
                itemClassName="location-modal__country-option"
                caretClassName="location-modal__country-caret"
                noItemsMessage=""
                getOptionValue={(option) => option.code}
                getOptionLabel={(option) => option.name}
                onChange={(countryCode, option) => handleCountryMenuSelect(countryCode, option?.name || DEFAULT_LOCATION_COUNTRY_OPTION.name)}
              />

              <PanelInputField
                className="location-modal__input-field location-modal__input-field--address"
                fieldRef={addressFieldRef}
                fieldProps={{ onBlur: handleAddressContainerBlur }}
                label="Address"
                id="location-address"
                type="text"
                value={addressValue}
                onChange={handleAddressChange}
                onKeyDown={handleAddressKeyDown}
                onFocus={() => {
                  if (suggestions.length > 0) {
                    openDropdown();
                  }
                }}
                placeholder="Start typing an address"
                disabled={isBusy}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={isAddressOpen}
                aria-controls={LOCATION_ADDRESS_LISTBOX_ID}
                aria-activedescendant={addressActiveDescendant}
              >
                <div
                  className="location-modal__address-status"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {addressStatus}
                </div>
              </PanelInputField>

              <label className="location-modal__privacy-toggle" htmlFor="location-hide-street">
                <input
                  id="location-hide-street"
                  type="checkbox"
                  checked={hideStreetAddress}
                  onChange={handleStreetPrivacyChange}
                  disabled={isBusy}
                />
                <span>Don&apos;t show street name</span>
              </label>

              {error && <div className="location-modal__error" role="alert">{error}</div>}

              {hasExistingLocation && (
                <div className="location-modal__content-actions">
                  <button
                    type="button"
                    className="location-modal__link-button"
                    onClick={handleClear}
                    disabled={isBusy}
                  >
                    Clear location
                  </button>
                </div>
              )}
            </div>

            <footer className="location-modal__footer">
              <Button
                variant="secondary"
                shape="pill"
                className="location-modal__action-button"
                type="button"
                disabled={isBusy}
                onClick={handleUseCurrentLocation}
              >
                {isLocating ? 'Locating…' : 'Use current location'}
              </Button>
              <div className="location-modal__footer-actions">
                <Button
                  variant="secondary"
                  shape="pill"
                  className="location-modal__secondary"
                  type="button"
                  disabled={isSaving || isAddressResolving}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  shape="pill"
                  className="location-modal__primary"
                  type="submit"
                  disabled={isSaving || isLocating || isMapResolving || isAddressResolving}
                >
                  {isSaving || isAddressResolving ? 'Saving…' : 'Save location'}
                </Button>
              </div>
            </footer>
          </form>
        </div>
      </PopupOverlay>
      {addressListbox ? createPortal(addressListbox, addressListboxPortalRoot) : null}
    </>
  );
};

LocationModal.propTypes = {
  userId: PropTypes.string,
  initialLocation: PropTypes.shape({
    country: PropTypes.string,
    countryCode: PropTypes.string,
    stateOrRegion: PropTypes.string,
    city: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    formatted: PropTypes.string,
    source: PropTypes.string,
  }),
  initialDisplay: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  onSave: PropTypes.func,
};

export default LocationModal;
